import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, useReactFlow, useStore } from '@xyflow/react';
import { Plus, Bot, MessageCircle, MessageSquare, Mail } from 'lucide-react';
import { AgentSummary } from '../api';
import { useWorkflowStore } from '../store';
import { addCenteredBranch, layoutCenteredBranches } from '../layout';

interface NodeActionPopoverProps {
  nodeId: string;
  positionX: number;
  positionY: number;
  /** Kept for compatibility with callers; the agent list was removed. */
  availableAgents?: AgentSummary[];
}

type ActionKind = 'whatsapp' | 'sms' | 'email';

// Default `data` for a freshly dropped action node. The message/template
// fields start blank; the server refuses to publish until they are filled, and
// the node card shows an "incomplete" warning until then.
const ACTION_DEFAULTS: Record<ActionKind, Record<string, any>> = {
  whatsapp: {
    nodeLabel: 'Send WhatsApp',
    whatsapp: { message_kind: 'free_text', message_body: '', recipient_source: 'customer_phone', recipient_field: '', template_id: '', template_name: '', template_mapping: {}, ai_agent_handoff: false, ai_agent_id: '', whatsapp_instance_name: '' },
  },
  sms: {
    nodeLabel: 'Send SMS',
    sms: { message_body: '', recipient_source: 'customer_phone', recipient_field: '' },
  },
  email: {
    nodeLabel: 'Send Email',
    email: { to: '', cc: [], subject: '', body: '' },
  },
};

export function NodeActionPopover({ nodeId, positionX, positionY }: NodeActionPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingNodeId, setPendingNodeId] = useState<string | null>(null);
  const measuredHeight = useStore(state => pendingNodeId
    ? state.nodeLookup.get(pendingNodeId)?.measured?.height ?? 0 : 0);
  // Screen-space anchor for the portaled menu, recomputed every time it opens.
  const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(null);
  const { setNodes, addEdges, getEdges, fitView } = useReactFlow();
  const setInspectorOpen = useWorkflowStore(state => state.setInspectorOpen);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // New cards have no measured height at insertion. Reconcile once, after
  // React Flow measures the card, before fitting the viewport to the result.
  useLayoutEffect(() => {
    if (!pendingNodeId || !measuredHeight) return;
    const edges = getEdges();
    setNodes(nodes => layoutCenteredBranches(nodeId, { x: positionX, y: positionY }, nodes, edges));
    setPendingNodeId(null);
    requestAnimationFrame(() => fitView({ duration: 300, maxZoom: 1, padding: 0.3 }));
  }, [pendingNodeId, measuredHeight, nodeId, positionX, positionY, getEdges, setNodes, fitView]);

  // Each react-flow node renders in its own stacking context, so a plain
  // z-index here would only ever win against siblings inside the same node.
  // Portal the menu to <body> and anchor it in screen space instead, so it
  // always floats above every node/edge regardless of render order.
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 256; // w-64
    const gap = 8;
    const spaceRight = window.innerWidth - rect.right;
    const openLeft = spaceRight < menuWidth + gap;
    setMenuPos({
      left: openLeft ? rect.left - menuWidth - gap : rect.right + gap,
      top: rect.top + rect.height / 2,
    });
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as globalThis.Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    // Close on canvas pan/zoom too — a screen-space anchor would otherwise
    // drift away from the node it was opened from.
    const handleClose = () => setIsOpen(false);
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside, true);
      window.addEventListener('wheel', handleClose, true);
      window.addEventListener('resize', handleClose);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      window.removeEventListener('wheel', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [isOpen]);

  // The same conditional edge NodeActionPopover has always drawn from the
  // source node to a newly created one.
  const connectNewNode = (newNodeId: string) => {
    const edges = getEdges();
    const existingFromSource = edges.filter(e => e.source === nodeId);
    const nextPriority = existingFromSource.length > 0
      ? Math.max(...existingFromSource.map(e => (e.data?.priority as number) || 0)) + 1
      : 0;

    addEdges({
      id: `edge-${Date.now()}`,
      source: nodeId,
      target: newNodeId,
      type: 'conditionEdge',
      data: {
        priority: nextPriority,
        conditions: [{ field: "", operator: "equals", value: "" }],
        delay: null,
      },
    });
  };

  const handleCreateAgent = () => {
    const newNodeId = `node-${Date.now()}`;

    const edges = getEdges();
    setNodes(nodes => addCenteredBranch(nodeId, { x: positionX, y: positionY }, nodes, edges, {
      id: newNodeId,
      type: 'agent',
      position: { x: positionX, y: positionY },
      data: { agentName: '', nodeLabel: 'New Step' },
    }));
    connectNewNode(newNodeId);
    setIsOpen(false);
    setPendingNodeId(newNodeId);
  };

  const handleCreateActionNode = (kind: ActionKind) => {
    const newNodeId = `node-${Date.now()}`;

    const edges = getEdges();
    setNodes(nodes => addCenteredBranch(nodeId, { x: positionX, y: positionY }, nodes, edges, {
      id: newNodeId,
      // React Flow node type — decides the card and, once serialised, the
      // backend `node_type` discriminator ("whatsapp" / "sms" / "email").
      type: kind,
      position: { x: positionX, y: positionY },
      data: { ...ACTION_DEFAULTS[kind] },
    }));
    connectNewNode(newNodeId);
    setIsOpen(false);
    setPendingNodeId(newNodeId);
  };

  return (
    <div
      className="absolute -right-5 top-1/2 -translate-y-1/2 z-50 flex items-center justify-center cursor-pointer"
      ref={triggerRef}
    >
      <div
        className="p-2"
        onClick={(e) => {
          e.stopPropagation();
          const next = !isOpen;
          setIsOpen(next);
          // The inspector panel and this popover both edit the graph in the
          // same spot on screen; closing one when the other opens avoids two
          // overlapping edit surfaces fighting for the same node.
          if (next) setInspectorOpen(false);
        }}
      >
        <Handle
          type="source"
          position={Position.Right}
          className="!static !transform-none flex h-6 w-6 items-center justify-center rounded-full bg-ink text-canvas transition-transform hover:scale-110 !border-0"
        >
          <Plus className="h-4 w-4 pointer-events-none" />
        </Handle>
      </div>

      {isOpen && menuPos && createPortal(
        <div
          ref={menuRef}
          className="nowheel nodrag fixed -translate-y-1/2 w-64 bg-elevated rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-line z-[9999] overflow-hidden flex flex-col cursor-default"
          style={{ left: menuPos.left, top: menuPos.top }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-3 flex flex-col gap-2.5">
            <div className="text-[10px] font-bold text-ink-3 uppercase tracking-wider">Add Step</div>

            <button
              onClick={handleCreateAgent}
              className="flex items-center gap-2 text-[12.5px] font-medium text-ink-2 hover:text-brand transition-colors w-full text-left"
            >
              <Bot className="w-3.5 h-3.5 text-ink-3" /> Agent (call)
            </button>
            <button
              onClick={() => handleCreateActionNode('whatsapp')}
              className="flex items-center gap-2 text-[12.5px] font-medium text-ink-2 hover:text-brand transition-colors w-full text-left"
            >
              <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" /> WhatsApp message
            </button>
            <button
              onClick={() => handleCreateActionNode('sms')}
              className="flex items-center gap-2 text-[12.5px] font-medium text-ink-2 hover:text-brand transition-colors w-full text-left"
            >
              <MessageSquare className="w-3.5 h-3.5 text-[#2563EB]" /> SMS message
            </button>
            <button
              onClick={() => handleCreateActionNode('email')}
              className="flex items-center gap-2 text-[12.5px] font-medium text-ink-2 hover:text-brand transition-colors w-full text-left"
            >
              <Mail className="w-3.5 h-3.5 text-[#EA4335]" /> Email
            </button>

            <p className="border-t border-line pt-2 text-[11px] leading-snug text-ink-3">
              To link an existing step, drag from the <span className="font-semibold text-ink-2">+</span> handle
              on this step's edge onto another step.
            </p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
